import { createActions, createReducer } from "reduxsauce"
import { put, call, takeLatest, select } from "redux-saga/effects"
import { get as apiGet, post as apiPost } from "api"
import { Types as HistoryTypes } from "../../../actions/app/history"
import { Types as DataTypes, Creators as modelCreators } from "model/data"
import { Types as PreferenceTypes } from "model/preferences"
import { getCompositeAssetSecurityIds } from "./selectors"
import { join } from "lodash"
import { Types as PortfolioRootTypes } from "modules/portfolio-root/model"
import { LOADING, OK, ERROR } from "helpers/data-statuses"

export const { Types, Creators } = createActions({
  search: ["account", "modelId", "value", "compositeAssetId", "compositeAssetType", "allowedTypes", "endDate", "securityIds"],

  searchSuccess: ["results"],
  searchError: ["error"],
  searchReset: [],
  open: [],
  close: [],
  up: [],
  down: [],
  setSelectedTab: ["selectedTab"],
  setModel: ["modelId"],
}, {
  prefix: "ompnt/modules/unified-search/",
})

const DEFAULT_STATE = {
  value: "",
  isOpen: false,
  selected: 0,
  results: null,
  modelId: null,
  error: null,
  selectedTab: "all",
  searchStatus: null,
  recentInstruments: [],
}

function handleSetModel(state, { modelId }) {
  return { ...state, modelId }
}

function handleSearch(state, { value }) {
  return { ...state, value, searchStatus: LOADING }
}

function handleSearchSuccess(state, { results }) {
  return { ...state, results, error: null, selected: 0, searchStatus: OK }
}

function handleSearchError(state, { error }) {
  return { ...state, results: null, error, selected: 0, searchStatus: ERROR }
}

function handleSearchReset(state) {
  return { ...state, results: null, selected: 0, searchStatus: null }
}

function handleOpen(state) {
  return { ...state, isOpen: true }
}

function handleClose(state) {
  return { ...state, isOpen: false, value: "", selected: 0, results: null }
}

function handleDown(state) {
  return { ...state, selected: state.selected + 1 }
}

function handleUp(state) {
  return { ...state, selected: state.selected - 1 }
}

function handleSetSelectedTab(state, { selectedTab }) {
  return { ...state, selectedTab }
}

const handleNavigate = (state) => {
  return { ...state, value: "", selected: 0, results: 0, isOpen: false }
}

const handleLoadSuccess = (state, { resource, data }) => {
  if (resource === "unifiedSearchFavoriteFactors") {
    const favoriteFactors = data[0]?.value
    return { ...state, favoriteFactors }
  }
  return state
}

export default createReducer(DEFAULT_STATE, {
  [Types.SET_MODEL]: handleSetModel,
  [Types.SEARCH]: handleSearch,
  [Types.SEARCH_SUCCESS]: handleSearchSuccess,
  [Types.SEARCH_ERROR]: handleSearchError,
  [Types.SEARCH_RESET]: handleSearchReset,
  [Types.OPEN]: handleOpen,
  [Types.CLOSE]: handleClose,
  [Types.DOWN]: handleDown,
  [Types.UP]: handleUp,
  [Types.SET_SELECTED_TAB]: handleSetSelectedTab,
  [HistoryTypes.NAVIGATE]: handleNavigate,
  [DataTypes.LOAD_SUCCESS]: handleLoadSuccess,
})

export function* search(action) {
  const { value, modelId, account, compositeAssetId, compositeAssetType, allowedTypes, endDate, securityIds } = action
  try {
    if (value) {
      let url, results
      if (securityIds && endDate) {
        // Search within a list of security ids
        const { modelProviderIds, swapTickers } = securityIds
        url = `/api/${account}/m/${modelId}/filterSecurities`
        results = yield call(apiPost, url, { value, modelProviderIds, swapTickers, endDate })
      } else if (compositeAssetId && compositeAssetType && endDate) {
        // Search within a composite asset
        const { compositeAssetSecurityIds: { modelProviderIds, swapTickers } } = yield select(state => ({
          compositeAssetSecurityIds: getCompositeAssetSecurityIds(state),
        }))
        url = `/api/${account}/m/${modelId}/filterSecurities`
        results = yield call(apiPost, url, { value, modelProviderIds, swapTickers, endDate })
      } else {
        // Search universe
        url = `/api/${account}/m/${modelId}/securities/search?value=${encodeURIComponent(value)}`
        if (compositeAssetId) {
          url += `&resourceId=${compositeAssetId}&resourceType=${compositeAssetType}`
        }
        if (allowedTypes) {
          url += `&allowedTypes=${join(allowedTypes, ",")}`
        }
        results = yield call(apiGet, url)
      }
      yield put(Creators.searchSuccess(results))
    } else {
      yield put(Creators.searchReset())
    }
  } catch (err) {
    yield put(Creators.searchError(err))
  }
}

export function* handleUpdatePreferencesSuccess({ action }) {
  const { type } = action || {}
  if (type === PortfolioRootTypes.TOGGLE_FAVORITE_FACTOR) {
    yield put(modelCreators.resetCollections(["unifiedSearchFavoriteFactors"]))
  }
}

export function* saga() {
  yield takeLatest(Types.SEARCH, search)
  yield takeLatest(PreferenceTypes.UPDATE_PREFERENCES_SUCCESS, handleUpdatePreferencesSuccess)
}
